import {
    BasicResponsiveImage,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
} from '@vp/swan';
import { NavigationTracking } from '@99designs/tracking';
import { sendNavigationEvent } from '@99designs/tracking';

interface ProductTileProps {
    title: string;
    imageSrc: string;
    imageAspectRatio: number;
    ctaLink: string;
    trackingData?: NavigationTracking & { crossPlatformTracking?: string };
}

const buildHref = (ctaLink: string, crossPlatformTracking?: string) => {
    if (!crossPlatformTracking) {
        return ctaLink;
    }

    const url = new URL(ctaLink, 'https://vistaprint.com');
    const newCtaLink = ctaLink.replace(url.search, '');
    const params = new URLSearchParams(
        crossPlatformTracking.startsWith('?') || crossPlatformTracking.startsWith('&')
            ? crossPlatformTracking.slice(1)
            : crossPlatformTracking
    );

    params.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    return newCtaLink + url.search;
};

export function ProductTile({
    title,
    imageSrc,
    imageAspectRatio,
    ctaLink,
    trackingData,
}: ProductTileProps) {
    const href = buildHref(ctaLink, trackingData?.crossPlatformTracking);

    return (
        <StandardTile
            skin="standard"
            imageWidth="full-bleed"
            render={({ className, children }) => (
                <a
                    href={href}
                    onClick={() => {
                        if (trackingData) {
                            const { crossPlatformTracking: _, ...navigationData } = trackingData;
                            sendNavigationEvent(navigationData);
                        }
                    }}
                    data-section={trackingData?.label}
                    data-translation={trackingData?.navigationDetail}
                    data-testid="product-tile"
                    className={className}
                >
                    {children}
                </a>
            )}
        >
            <StandardTileImage>
                <BasicResponsiveImage
                    aspectRatio={imageAspectRatio}
                    src={imageSrc}
                    alt=""
                    role="presentation"
                />
            </StandardTileImage>
            <StandardTileContents>
                <StandardTileName>{title}</StandardTileName>
            </StandardTileContents>
        </StandardTile>
    );
}
